import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { apiClient } from '../config/api';
import { useAuth } from './AuthContext';

interface HeartContextType {
  balance: number;
  isLoading: boolean;
  lastUpdated: Date | null;
  refreshBalance: () => Promise<void>;
  addHearts: (amount: number) => void;
  consumeHeart: (transactionType: string, referenceId?: number) => Promise<{
    success: boolean;
    message: string;
    consumedHearts: number;
    currentBalance: number;
  }>;
}

const HeartContext = createContext<HeartContextType | undefined>(undefined);

interface HeartProviderProps {
  children: ReactNode;
}

export const HeartProvider: React.FC<HeartProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // 잔액 조회
  const refreshBalance = useCallback(async () => {
    try {
      const data = await apiClient.getHeartBalance();
      setBalance(data.balance);
      setLastUpdated(new Date());
    } catch (error) {
      // 에러 발생 시 기존 잔액 유지
    }
  }, []);

  // 컴포넌트 마운트 시 잔액 조회
  useEffect(() => {
    const token = localStorage.getItem('accessToken');

    if (token) {
      refreshBalance().finally(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [refreshBalance]);

  // 유저 변경 시 잔액 초기화 또는 재조회
  useEffect(() => {
    if (user === null) {
      setBalance(0);
      setLastUpdated(null);
    } else {
      refreshBalance();
    }
  }, [user?.id]);

  // 하트 추가 (구매/리워드 후 즉시 반영용)
  const addHearts = useCallback((amount: number) => {
    setBalance(prev => prev + amount);
    setLastUpdated(new Date());
  }, []);

  // 하트 소비
  const consumeHeart = useCallback(async (transactionType: string, referenceId?: number) => {
    const result = await apiClient.consumeHeart({ transactionType, referenceId });
    if (result.success) {
      setBalance(result.currentBalance);
      setLastUpdated(new Date());
    }
    return result;
  }, []);

  const value = {
    balance,
    isLoading,
    lastUpdated,
    refreshBalance,
    addHearts,
    consumeHeart,
  };

  return <HeartContext.Provider value={value}>{children}</HeartContext.Provider>;
};

export const useHeart = () => {
  const context = useContext(HeartContext);
  if (!context) {
    throw new Error('useHeart must be used within a HeartProvider');
  }
  return context;
};
